import { useMemo } from 'react';

import type { Hco, Hcphco } from '@/core/graphql/generated/types';

import { useProfileMetaData } from '../../Hcp/HcpCard/_hooks/useProfileMetaData';

export type HierarchyNode = {
	hco: Hco;
	isPrimary: boolean;
	isCurrent: boolean;
};

export const useParentHierarchyNodes = (
	profile: Hcphco | null
): HierarchyNode[] => {
	const hco = profile?.__typename === 'HCO' ? profile : null;
	const { primaryAffiliation } = useProfileMetaData(profile);

	const mainOrg =
		hco?.mainOrganization?.__typename === 'HCO' ? hco.mainOrganization : null;

	const topOrg =
		hco?.topOrganization?.__typename === 'HCO' ? hco.topOrganization : null;

	return useMemo(() => {
		if (!hco) return [];

		const sources = [
			{ hco: topOrg, isPrimary: false, isCurrent: false },
			{ hco: mainOrg, isPrimary: false, isCurrent: false },
			{ hco: primaryAffiliation, isPrimary: true, isCurrent: false },
			{ hco, isPrimary: false, isCurrent: true }
		];

		const map = new Map<string, HierarchyNode>();
		for (const source of sources) {
			const id = source.hco?.vid_V;
			if (!id) continue;

			const existing = map.get(id);
			map.set(id, {
				hco: source.hco ?? existing?.hco ?? hco,
				isPrimary: !!existing?.isPrimary || source.isPrimary,
				isCurrent: !!existing?.isCurrent || source.isCurrent
			});
		}

		const rank = (node: HierarchyNode) =>
			node.isCurrent ? 2 : node.isPrimary ? 1 : 0;
		const result = Array.from(map.values()).sort((a, b) => rank(a) - rank(b));

		if (result.length <= 1) return [];

		return result;
	}, [hco, primaryAffiliation, mainOrg, topOrg]);
};
